/**
 * Canonical API Client
 * Production-ready HTTP client with comprehensive error handling, retries, and caching
 */

import { getApiConfig, isDebugEnabled } from "../config/api-config";
import { TokenManager } from "../secure-storage";
import {
	createApiError,
	type ErrorContext,
	getRetryDelay,
	logApiError,
	NetworkError,
	shouldRetry,
	TimeoutError,
} from "./errors";
import type { RequestOptions } from "./types";

export interface HttpClientOptions {
	baseURL?: string;
	timeout?: number;
	retries?: number;
	defaultHeaders?: Record<string, string>;
}

export class HttpClient {
	private tokenManager: TokenManager;
	private config = getApiConfig();
	private requestCache = new Map<string, { data: any; expiry: number }>();

	constructor(private options: HttpClientOptions = {}) {
		this.tokenManager = new TokenManager();
	}

	// ==================== Core HTTP Methods ====================

	async get<T>(path: string, options?: RequestOptions): Promise<T> {
		return this.request<T>("GET", path, undefined, options);
	}

	async post<T>(
		path: string,
		data?: any,
		options?: RequestOptions,
	): Promise<T> {
		return this.request<T>("POST", path, data, options);
	}

	async put<T>(path: string, data?: any, options?: RequestOptions): Promise<T> {
		return this.request<T>("PUT", path, data, options);
	}

	async patch<T>(
		path: string,
		data?: any,
		options?: RequestOptions,
	): Promise<T> {
		return this.request<T>("PATCH", path, data, options);
	}

	async delete<T>(path: string, options?: RequestOptions): Promise<T> {
		return this.request<T>("DELETE", path, undefined, options);
	}

	// ==================== Request Implementation ====================

	private async request<T>(
		method: string,
		path: string,
		data?: any,
		options: RequestOptions = {},
	): Promise<T> {
		const config = this.config;
		const baseURL = this.options.baseURL || config.baseURL;
		const url = path.startsWith("http") ? path : `${baseURL}${path}`;

		// Check cache for GET requests
		if (method === "GET" && config.features.enableCaching) {
			const cached = this.getFromCache(url);
			if (cached) {
				if (isDebugEnabled()) {
					console.log("🎯 Cache hit:", url);
				}
				return cached as T;
			}
		}

		const requestOptions: RequestInit = {
			method,
			headers: await this.buildHeaders(options.headers),
			body: data ? JSON.stringify(data) : undefined,
			signal: options.signal,
		};

		const context: ErrorContext = {
			method,
			url,
			headers: requestOptions.headers as Record<string, string>,
			body: data,
		};

		let attempt = 0;
		const maxRetries = options.retries ?? config.retries;
		const timeout = options.timeout ?? config.timeout;

		while (attempt <= maxRetries) {
			try {
				if (isDebugEnabled()) {
					console.log(`🚀 ${method} ${url}`, data ? { body: data } : "");
				}

				const response = await this.fetchWithTimeout(
					url,
					requestOptions,
					timeout,
				);
				const result = await this.handleResponse<T>(response, url, method);

				// Cache successful GET responses
				if (method === "GET" && config.features.enableCaching) {
					this.setCache(url, result);
				}

				if (isDebugEnabled()) {
					console.log(`✅ ${method} ${url}`, result);
				}

				return result;
			} catch (error) {
				const apiError =
					error instanceof Error
						? this.convertToApiError(error, url, method)
						: createApiError(new Response(), error);

				logApiError(apiError, context);

				// Check if we should retry
				if (
					shouldRetry(apiError, attempt, maxRetries) &&
					config.features.enableRetries
				) {
					attempt++;
					const delay = getRetryDelay(attempt - 1);

					if (isDebugEnabled()) {
						console.log(
							`🔄 Retrying ${method} ${url} in ${delay}ms (attempt ${attempt}/${maxRetries + 1})`,
						);
					}

					await this.sleep(delay);
					continue;
				}

				throw apiError;
			}
		}

		throw new Error("Max retries exceeded");
	}

	// ==================== Response Handling ====================

	private async handleResponse<T>(
		response: Response,
		_url: string,
		method: string,
	): Promise<T> {
		const requestId = response.headers.get("x-request-id") || undefined;

		if (!response.ok) {
			let errorData: any;
			try {
				errorData = await response.json();
			} catch {
				errorData = { message: response.statusText };
			}
			throw createApiError(response, errorData, requestId);
		}

		// Handle empty responses for void methods
		if (response.status === 204 || method === "DELETE") {
			return null as unknown as T;
		}

		try {
			const data = await response.json();
			return this.unwrapResponse<T>(data);
		} catch (_error) {
			throw new NetworkError("Failed to parse response as JSON", requestId);
		}
	}

	private unwrapResponse<T>(data: any): T {
		// Handle wrapped API responses
		if (data && typeof data === "object" && "data" in data) {
			return data.data as T;
		}
		return data as T;
	}

	// ==================== Header Management ====================

	private async buildHeaders(
		customHeaders?: Record<string, string>,
	): Promise<Headers> {
		const headers = new Headers({
			"Content-Type": "application/json",
			Accept: "application/json",
			"X-Client-Version": "1.0.0",
			...this.options.defaultHeaders,
			...customHeaders,
		});

		// Add authentication header if token exists
		const token = this.tokenManager.getToken();
		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		return headers;
	}

	// ==================== Utilities ====================

	private async fetchWithTimeout(
		url: string,
		options: RequestInit,
		timeout: number,
	): Promise<Response> {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		try {
			const response = await fetch(url, {
				...options,
				signal: controller.signal,
			});
			return response;
		} catch (error) {
			if (error instanceof Error && error.name === "AbortError") {
				throw new TimeoutError(timeout);
			}
			throw new NetworkError(
				error instanceof Error ? error.message : "Network error",
			);
		} finally {
			clearTimeout(timeoutId);
		}
	}

	private convertToApiError(error: Error, _url: string, _method: string): any {
		if (error.name === "AbortError") {
			return new TimeoutError(this.config.timeout);
		}

		if (error.name === "TypeError" || error.message.includes("fetch")) {
			return new NetworkError(error.message);
		}

		return createApiError(new Response(), {
			code: "UNKNOWN_ERROR",
			message: error.message,
			details: { url: _url, method: _method },
		});
	}

	private sleep(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	// ==================== Caching ====================

	private getCacheKey(url: string): string {
		return url;
	}

	private getFromCache<T>(url: string): T | null {
		const key = this.getCacheKey(url);
		const cached = this.requestCache.get(key);

		if (!cached) {
			return null;
		}

		if (Date.now() > cached.expiry) {
			this.requestCache.delete(key);
			return null;
		}

		return cached.data as T;
	}

	private setCache(url: string, data: any, ttl = 300000): void {
		// 5 minutes default
		const key = this.getCacheKey(url);
		const expiry = Date.now() + ttl;

		this.requestCache.set(key, { data, expiry });

		// Simple cache size management
		if (this.requestCache.size > 100) {
			const firstKey = this.requestCache.keys().next().value;
			if (firstKey) {
				this.requestCache.delete(firstKey);
			}
		}
	}

	// ==================== Public Utilities ====================

	public clearCache(): void {
		this.requestCache.clear();
	}

	public getBaseURL(): string {
		return this.options.baseURL || this.config.baseURL;
	}
}
